var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import Button from "../../atoms/button/button";
import Card from "../../atoms/card/card";
import Bar from "../../molecules/bar/bar";
import NavBar from "../../organisms/navBar/navBar";
import LoadingOverlay from "../../atoms/loadingOverlay/loadingOverlay";
import "./dashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { getQuesAttemptDetails, getStatsTileData, clearDashboardForProfileSwitch, } from "../../../redux/actions/dashboard";
import { useLocation, useNavigate } from "react-router";
import DashboardStats from "./dashboardStats";
import DashboardGraps from "./dashboardGraps";
import PageHeader from "../../organisms/pageHeader/pageHeader";
import { getQuestionnaireHistory } from "../../../redux/actions/questionnaireHistoryActions";
import { Status } from "../../../redux/constants/status";
/**
 * This returns the ordinals for a given date.
 * 1 becomes st
 * 2 becomes nd
 * etc...
 * @param {number} number The date.
 */
function nthNumber(number) {
    return number > 0
        ? ["th", "st", "nd", "rd"][(number > 3 && number < 21) || number % 10 > 3 ? 0 : number % 10]
        : "";
}
/**
 * The Dashboard Page
 * This will need to call all relevant APIs to get the values.
 * @param props
 * @returns
 */
function Dashboard() {
    var dateObj = new Date();
    var day = dateObj.getDate();
    var month = dateObj.toLocaleString("default", { month: "long" });
    var year = dateObj.getFullYear();
    var userToken = useSelector(function (state) { return state.rootReducer.userReducer.loginData; });
    var username = useSelector(function (state) { return state.rootReducer.userReducer.username; });
    var user_type = useSelector(function (state) { return state.rootReducer.userReducer.userType; });
    var needs_questionnaire = useSelector(function (state) { return state.rootReducer.dashboardReducer.needs_questionnaire; });
    var numberOfAttempts = useSelector(function (state) {
        return state.rootReducer.questionnaireHistoryReducer.numberOfAttempts;
    });
    var complete = useSelector(function (state) { return state.rootReducer.questionnaireHistoryReducer.complete; });
    var state = useLocation().state;
    var dispatch = useDispatch();
    var navigate = useNavigate();
    // Local loading state for immediate feedback
    var _a = useState(false), isLocalLoading = _a[0], setIsLocalLoading = _a[1];
    useEffect(function () {
        if (user_type === "jobseeker") {
            dispatch(getQuestionnaireHistory(username));
        }
    }, [user_type, username]);
    // Get the current viewing user from state or fallback to logged-in user
    var currentViewingUser = state && state.viewUser ? state.viewUser : username;
    // Get the currently displayed username to detect if we're switching profiles
    var currentDisplayedUsername = useSelector(function (state) {
        return state.rootReducer.dashboardReducer.viewing_username ||
            state.rootReducer.dashboardReducer.username;
    });
    var _b = useState(""), displayName = _b[0], setDisplayName = _b[1];
    useEffect(function () {
        if (username) {
            var saved = localStorage.getItem("displayName_".concat(username));
            setDisplayName(saved || username);
        }
    }, [username]);
    var handleNameChange = function (newName) {
        setDisplayName(newName);
        localStorage.setItem("displayName_".concat(username), newName);
    };
    // Get status first before using it
    var statTileFetchStatus = useSelector(function (state) { return state.rootReducer.dashboardReducer.status; });
    // Check if we're switching to a different user (showing loading immediately)
    var isSwitchingProfiles = currentViewingUser &&
        currentDisplayedUsername &&
        currentViewingUser !== currentDisplayedUsername;
    // Also check if we have a viewUser from navigation state (immediate loading)
    // But only consider it "navigating" if we haven't loaded the data yet
    var isNavigatingToNewUser = !!(state &&
        state.viewUser &&
        statTileFetchStatus === Status.Loading);
    // Additional check: if we're currently loading and have a viewUser, show loading
    // OR if we have a viewUser in navigation state and it's different from current displayed user
    var isLoadingWithViewUser = !!(state &&
        state.viewUser &&
        (statTileFetchStatus === Status.Loading ||
            state.viewUser !== currentDisplayedUsername));
    if (!userToken) {
        // User has not logged in, no auth token
        // Redirect to Login
        useEffect(function () {
            navigate("/login");
        }, []);
        // } else if (numberOfAttempts === 0 && user_type === "jobseeker") {
        //   useEffect(() => {
        //     navigate("/");
        //   }, []);
    }
    else {
        // Now we know that the user is logged in. Perform the API calls
        useEffect(function () {
            if (currentViewingUser) {
                // Set local loading state immediately
                setIsLocalLoading(true);
                // Clear old data immediately when switching profiles
                if (isSwitchingProfiles) {
                    dispatch(clearDashboardForProfileSwitch());
                }
                // Single API call for dashboard data - getStatsTileData now handles both dashboard stats and caseload data
                dispatch(getStatsTileData(currentViewingUser));
            }
        }, [currentViewingUser, dispatch, isSwitchingProfiles]);
    }
    // Clear local loading when API call completes
    useEffect(function () {
        if (statTileFetchStatus === Status.Successful ||
            statTileFetchStatus === Status.Error) {
            setIsLocalLoading(false);
        }
    }, [statTileFetchStatus]);
    var data = useSelector(function (state) { return state.rootReducer.dashboardReducer.graphData; });
    var groupedData = useSelector(function (state) { return state.rootReducer.dashboardReducer.groupedGraphData; });
    var rightSide = useSelector(function (state) { return state.rootReducer.dashboardReducer.score; });
    var quesAttemptDetails = useSelector(function (state) { return state.rootReducer.dashboardReducer.quesAttemptDetails; });
    var logged_in_user_type = useSelector(function (state) { return state.rootReducer.dashboardReducer.logged_in_user_type; });
    var viewing_user_type = useSelector(function (state) { return state.rootReducer.dashboardReducer.viewing_user_type; });
    var role = useSelector(function (state) { return state.rootReducer.userReducer.role; });
    useEffect(function () {
        if (currentViewingUser && (user_type === "jobseeker" || viewing_user_type === "jobseeker")) {
            dispatch(getQuesAttemptDetails(currentViewingUser));
        }
    }, [currentViewingUser, user_type, viewing_user_type, dispatch]);
    useEffect(function () {
        if (needs_questionnaire && user_type === "jobseeker") {
            navigate("/questionnaire/resume");
        }
    }, [needs_questionnaire, user_type, navigate]);
    var handleMilestoneQuestionnaire = function () {
        if (quesAttemptDetails &&
            quesAttemptDetails.milestone_questionnaire_ready &&
            !quesAttemptDetails.milestone_completed) {
            navigate("/questionnaire/resume");
        }
    };
    var handleQuestionnaire = function () {
        var milestonePending = !!(quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.milestone_questionnaire_ready) &&
            !(quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.milestone_completed);
        if (quesAttemptDetails &&
            quesAttemptDetails.second_attempt_ready &&
            !quesAttemptDetails.completed_twice &&
            !milestonePending) {
            navigate("/questionnaire/resume");
        }
    };
    var viewing_username = useSelector(function (state) { return state.rootReducer.dashboardReducer.viewing_username; });
    var NON_JOBSEEKER_TYPES = ["adviser", "manager", "organisation", "superorg"];
    var noData = !!logged_in_user_type &&
        NON_JOBSEEKER_TYPES.includes(logged_in_user_type) &&
        statTileFetchStatus === Status.Successful &&
        (!data || (Array.isArray(data) && data.length === 0)) &&
        !groupedData;
    useEffect(function () {
        if (role === "jobseeker" && numberOfAttempts < 1 && complete) {
            navigate("/questionnaire/resume");
        }
    }, [role, numberOfAttempts, complete, navigate]);
    var isProfileSwitching = statTileFetchStatus === Status.Loading ||
        isSwitchingProfiles ||
        isNavigatingToNewUser ||
        isLocalLoading ||
        isLoadingWithViewUser;
    // Essential debug log for loading overlay issues
    if (isProfileSwitching) {
        console.log("🔄 Dashboard: Loading overlay visible for user:", currentViewingUser);
    }
    return (_jsxs("div", __assign({ className: "dashboard" }, { children: [_jsx(NavBar, { viewing_user: viewing_user_type, logged_in_user: logged_in_user_type, viewing_username: viewing_username }), _jsxs("div", __assign({ className: "dashboard-page-template-right", style: { position: "relative" } }, { children: [_jsx(LoadingOverlay, { isVisible: isProfileSwitching, message: state && state.viewUser
                            ? "Loading ".concat(state.viewUser, "'s dashboard...")
                            : "Loading profile data..." }), logged_in_user_type === viewing_user_type ? (_jsx(PageHeader, { title: "".concat(displayName, " Dashboard"), sticky: true, editable: displayName, onNameChange: handleNameChange })) : (_jsx(PageHeader, { title: "".concat(state && state.viewUser, "'s Dashboard"), backButton: "Back to Manage Users", onClick: function () {
                            navigate("/manage", {
                                state: { viewUser: username },
                            });
                        }, sticky: true })), _jsxs("div", __assign({ className: "relative" }, { children: [_jsx(DashboardStats, { noData: noData }), _jsxs(Bar, __assign({ title: "Employability Area", className: "my-3 py-2" }, { children: [_jsx(Card, { children: "".concat(day).concat(nthNumber(day), " ").concat(month, " ").concat(year) }), logged_in_user_type == "jobseeker" &&
                                        quesAttemptDetails &&
                                        quesAttemptDetails.milestone_questionnaire_ready &&
                                        !quesAttemptDetails.milestone_completed && (_jsx(Button, __assign({ onClick: handleMilestoneQuestionnaire, className: "h-16 w-44 ml-3" }, { children: "Progression Milestone" }))), logged_in_user_type == "jobseeker" &&
                                        quesAttemptDetails &&
                                        quesAttemptDetails.second_attempt_ready &&
                                        !quesAttemptDetails.completed_twice &&
                                        !(quesAttemptDetails.milestone_questionnaire_ready &&
                                            !quesAttemptDetails.milestone_completed) && (_jsx(Button, __assign({ onClick: handleQuestionnaire, className: "h-16 w-44 ml-3" }, { children: "Questionnaire 2" })))] })), _jsx(DashboardGraps, { data: data, groupedData: groupedData, rightSide: rightSide, userType: user_type, noData: noData })] }))] }))] })));
}
export default Dashboard;
